import { Book, ChevronRight, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

// Temporary manually defined until orval re-generates cleanly in background
interface Catalog {
  id: string;
  academicYear: number;
  title: string;
  version: number;
  status: 'draft' | 'preview' | 'published' | 'archived';
  publishedAt: string | null;
  updatedAt: string;
}

export default function CatalogList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Mock data for now since we don't have the API hook ready-to-use without regeneration check
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const catalogs: Catalog[] = [
    {
      id: '2026-v1',
      academicYear: 2026,
      title: '2026年度 学生募集要項',
      version: 1,
      status: 'draft',
      publishedAt: null,
      updatedAt: '2025-10-01T10:00:00Z',
    },
    {
      id: '2025-v3',
      academicYear: 2025,
      title: '2025年度 学生募集要項',
      version: 3,
      status: 'published',
      publishedAt: '2024-06-01T09:00:00Z',
      updatedAt: '2024-06-01T09:00:00Z',
    },
  ];

  /*
  // Future Integration
  const [loading, setLoading] = useState(true);
  useEffect(() => {
     // Fetch logic
  }, []);
  */

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'published':
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-200">Published</Badge>;
      case 'preview':
        return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200">Preview</Badge>;
      case 'draft':
        return <Badge variant="outline">Draft</Badge>;
      case 'archived':
        return <Badge variant="secondary">Archived</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 flex items-center gap-2">
            <Book className="w-8 h-8 text-indigo-600" />
            募集要項・入試カタログ
          </h1>
          <p className="text-gray-500 mt-1">年度ごとの入試情報のバージョン管理を行います。(Annual Catalog)</p>
        </div>
        <Button onClick={() => navigate('new')} className="gap-2">
          <Plus className="w-4 h-4" />
          新規年度登録
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Catalog History</CardTitle>
            <CardDescription>各年度の募集要項バージョン一覧</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">年度</TableHead>
                  <TableHead>タイトル</TableHead>
                  <TableHead>Ver.</TableHead>
                  <TableHead>ステータス</TableHead>
                  <TableHead>公開日時</TableHead>
                  <TableHead>更新日時</TableHead>
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {catalogs.map((catalog) => (
                  <TableRow
                    key={catalog.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(catalog.id)}
                  >
                    <TableCell className="font-bold">{catalog.academicYear}</TableCell>
                    <TableCell>
                      <div className="font-medium">{catalog.title}</div>
                      <div className="text-xs text-gray-400">
                        {t('common.id')}: {catalog.id}
                      </div>
                    </TableCell>
                    <TableCell>v{catalog.version}</TableCell>
                    <TableCell>{getStatusBadge(catalog.status)}</TableCell>
                    <TableCell>{catalog.publishedAt || '-'}</TableCell>
                    <TableCell>{new Date(catalog.updatedAt).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm">
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
